import { useQuery } from "@tanstack/react-query";
import { useApi } from "providers";
import { useMeeting } from "providers";
import React, { PropsWithChildren, startTransition, useEffect, useMemo, useState } from "react";

const logger = createLogger("OutputDocumentsProvider");

import { createLogger } from "logging";

import { OutputDocumentsContext } from "./OutputDocumentsContext";

export const OutputDocumentsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { outputDocumentsApi } = useApi();
  const { meetingId } = useMeeting();

  const [meetingOpen, setMeetingOpen] = useState(false);
  const [agendaItemOpen, setAgendaItemOpen] = useState(false);
  const [showAgendaItemsWarnings, setShowAgendaItemsWarnings] = useState(false);
  const [showParticipantsWarnings, setShowParticipantsWarnings] = useState(false);

  const queryResult = useQuery({
    queryKey: [`Meeting|${meetingId}|OutputDocuments`],
    queryFn: async () => {
      if (!meetingId) return null;
      const data = outputDocumentsApi.getAll(Number(meetingId));
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!meetingId,
  });

  const isLoading = queryResult.isPending;
  const meetingLinks = useMemo(() => queryResult.data?.meeting ?? [], [queryResult.data?.meeting]);
  const agendaItemLinks = useMemo(() => queryResult.data?.agendaItems ?? [], [queryResult.data?.agendaItems]);

  useEffect(() => {
    if (!(meetingLinks && meetingLinks.length > 0)) {
      return;
    }

    startTransition(() => {
      const updateParticipants = meetingLinks.find((x) => !!x && x.name === "MeetingUpdateParticipants");
      if (updateParticipants && !updateParticipants.disabled) {
        setShowParticipantsWarnings(true);
      }

      const updateAgendaItems = meetingLinks.find((x) => !!x && x.name === "MeetingUpdateAgendaItems");
      if (updateAgendaItems && !updateAgendaItems.disabled) {
        setShowAgendaItemsWarnings(true);
      }
    });
  }, [meetingLinks]);

  const contextValue = useMemo(
    () => ({
      meetingLinks,
      agendaItemLinks,
      isLoading,
      meetingOpen,
      setMeetingOpen,
      agendaItemOpen,
      setAgendaItemOpen,
      showAgendaItemsWarnings,
      showParticipantsWarnings,
    }),
    [
      meetingLinks,
      agendaItemLinks,
      isLoading,
      meetingOpen,
      setMeetingOpen,
      agendaItemOpen,
      setAgendaItemOpen,
      showAgendaItemsWarnings,
      showParticipantsWarnings,
    ]
  );

  return <OutputDocumentsContext.Provider value={contextValue}>{children}</OutputDocumentsContext.Provider>;
};
